import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Send, Loader2, MessageCircle } from "lucide-react";

/**
 * PrintJobChat — collapsible message thread scoped to a single print-job.
 * Used on both the buyer's My Print Jobs tab and the vendor's Jobs tab.
 * Polls every 15s while expanded so both parties see new messages without
 * a full page reload. Author-side messages align right, other-side left.
 */
export default function PrintJobChat({ jobId, me, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const listRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/print-jobs/${jobId}/messages`);
      setMsgs(data);
    } catch (e) {
      // silent — chat is a soft feature
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [open, jobId]);

  useEffect(() => {
    if (open && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [msgs, open]);

  const send = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/print-jobs/${jobId}/messages`, { body });
      setMsgs((m) => [...m, data]);
      setText("");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="mt-3 border-t border-border pt-3" data-testid={`chat-${jobId}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`chat-toggle-${jobId}`}
        className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1.5"
      >
        <MessageCircle className="h-3.5 w-3.5" /> {open ? "Hide chat" : `Chat ${msgs.length ? `(${msgs.length})` : ""}`}
      </button>

      {open && (
        <div className="mt-2 rounded-md border border-border bg-background">
          <div
            ref={listRef}
            className="max-h-56 min-h-[80px] overflow-y-auto p-3 space-y-2"
            data-testid={`chat-list-${jobId}`}
          >
            {loading && !msgs.length && (
              <div className="flex items-center justify-center py-6 text-muted-foreground text-xs">
                <Loader2 className="h-3 w-3 animate-spin mr-2" /> Loading messages…
              </div>
            )}
            {!loading && msgs.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-6">
                No messages yet — start the conversation with print specs, sizing, or delivery notes.
              </p>
            )}
            {msgs.map((m) => {
              const mine = m.author_role === me;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[80%] rounded-lg px-3 py-2 text-xs leading-relaxed break-words ${
                      mine ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"
                    }`}
                  >
                    <p className="whitespace-pre-wrap">{m.body}</p>
                    <p className={`mt-1 text-[9px] uppercase tracking-widest font-mono ${mine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                      {m.author_role} · {new Date(m.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
          <form onSubmit={send} className="flex gap-2 p-2 border-t border-border bg-secondary/30">
            <input
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type a message…"
              data-testid={`chat-input-${jobId}`}
              className="flex-1 border border-border rounded-md p-2 text-xs bg-background"
              maxLength={2000}
            />
            <button
              type="submit"
              disabled={posting || !text.trim()}
              data-testid={`chat-send-${jobId}`}
              className="bg-primary text-primary-foreground rounded-md px-3 py-2 text-xs font-semibold disabled:opacity-50 inline-flex items-center gap-1"
            >
              {posting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
